import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, AlertTriangle } from 'lucide-react';
import { TeamLogo } from './TeamLogo';
import { ForfeitBadge } from './ForfeitBadge';
import { LoadingSpinner } from './LoadingSpinner';
import { cn } from '@/lib/utils';

interface TeamGameCardProps {
  teamName?: string;
  teamLogo?: string;
  teamLabel: string;
  isConfirmed: boolean;
  isForfeited?: boolean;
  forfeitReason?: string;
  participantCount: number;
  minimumPlayers: number;
  side: 'home' | 'away';
  className?: string;
  loading?: boolean;
}

export const TeamGameCard = ({
  teamName,
  teamLogo,
  teamLabel,
  isConfirmed,
  isForfeited,
  forfeitReason,
  participantCount,
  minimumPlayers,
  side,
  className,
  loading = false
}: TeamGameCardProps) => {
  const isUnderMinimum = participantCount < minimumPlayers;
  
  return (
    <Card className={cn(
      "transition-all duration-200 hover:shadow-md",
      isForfeited && "bg-destructive/5 border-destructive/20",
      loading && "opacity-50",
      className
    )}>
      <CardContent className="p-3 sm:p-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="sm" text="Loading team info..." />
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 sm:gap-3">
              <TeamLogo 
                teamName={teamName || teamLabel}
                logoUrl={teamLogo}
                size="lg"
                className="flex-shrink-0"
              />
              
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm sm:text-base truncate">
                  {teamName && !['TBD', 'Open', 'Loading...'].includes(teamName) ? 
                    `${teamLabel}: ${teamName}` : 
                    teamName === 'Open' ? `${teamLabel} Team (Open)` :
                    teamName === 'Loading...' ? `${teamLabel} Team (Loading...)` :
                    `${teamLabel} Team (Available)`
                  }
                </div>
                
                <div className="flex items-center gap-2 mt-1 sm:mt-2">
                  <div className={cn(
                    "flex items-center gap-1 text-xs",
                    isUnderMinimum && !isForfeited && "text-destructive"
                  )}>
                    <Users className="h-3 w-3 flex-shrink-0" />
                    <span className="font-medium">{participantCount}/{minimumPlayers}</span>
                  </div>
                  
                  {isConfirmed && !isForfeited && (
                    <Badge variant="outline" className="text-xs py-0 px-1 sm:px-2">
                      Confirmed
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            
            {isForfeited && (
              <div className="mt-3">
                <ForfeitBadge reason={forfeitReason} variant="detailed" />
              </div>
            )}
            
            {isUnderMinimum && !isForfeited && (
              <div className="mt-3 p-2 bg-destructive/10 border border-destructive/20 rounded text-xs text-destructive flex items-center gap-2">
                <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">Below minimum players required</span>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};